import React from 'react';
import { clsx } from 'clsx';

export interface StatProps {
  title: string;
  value: string | number;
  desc?: string;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  icon?: React.ReactNode;
  variant?: 'default' | 'primary' | 'secondary' | 'accent';
  className?: string;
}

export const Stat: React.FC<StatProps> = ({
  title,
  value,
  desc,
  trend,
  trendValue,
  icon,
  variant = 'default',
  className,
}) => {
  const trendIcons = {
    up: (
      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M7 11l5-5m0 0l5 5m-5-5v12"
        />
      </svg>
    ),
    down: (
      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M17 13l-5 5m0 0l-5-5m5 5V6"
        />
      </svg>
    ),
    neutral: null,
  };

  const variantClasses = {
    default: '',
    primary: 'text-primary',
    secondary: 'text-secondary',
    accent: 'text-accent',
  };

  return (
    <div className={clsx('stat', className)}>
      {icon && (
        <div className={clsx('stat-figure', variant !== 'default' && variantClasses[variant])}>
          {icon}
        </div>
      )}
      <div className="stat-title">{title}</div>
      <div className={clsx('stat-value', variant !== 'default' && variantClasses[variant])}>
        {value}
      </div>
      {(desc || trend) && (
        <div className="stat-desc">
          {trend && trend !== 'neutral' && (
            <span
              className={clsx(
                'inline-flex items-center gap-1',
                trend === 'up' && 'text-success',
                trend === 'down' && 'text-error'
              )}
            >
              {trendIcons[trend]}
              {trendValue && <span>{trendValue}</span>}
            </span>
          )}
          {desc && <span className={trend && trend !== 'neutral' ? 'ml-2' : ''}>{desc}</span>}
        </div>
      )}
    </div>
  );
};

export interface StatsProps {
  children: React.ReactNode;
  className?: string;
}

export const Stats: React.FC<StatsProps> = ({ children, className }) => {
  return <div className={clsx('stats shadow', className)}>{children}</div>;
};
